import { ChangeEvent, RefObject, SyntheticEvent, useEffect, useRef, useState } from "react"
import useAuthContext from "../../hooks/useAuthContext"
import Permissions from "../../constants/Permissions"
import PlaceService from "../../services/PlaceService"
import EventService from "../../services/EventService"
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap"
import EInvoiceProviderSelect from "../../components/EInvoiceProviderSelect"
import DatePicker from "../../components/DatePicker"
import { EInvoiceConfigurationExamples } from "../../constants/EInvoiceProvider"

export interface IEInvoiceSettings {
    eInvoiceProviderName: string
    eInvoiceConfigurations: string
    eInvoiceAutoPublish: boolean
    eInvoiceEnabled: boolean
    issueInvoiceAfterMinutes: number | null
    invoiceIssueStartDate: string | null
}

interface IEInvoiceSettingsModalProp {
    providerId: number | null
    objectName: "Event" | "Place"
    objectId: number | null
    show: boolean
    title: string
    onClose: () => void
}

function EInvoiceSettingsModal(props: IEInvoiceSettingsModalProp) {
    const { hasPermission } = useAuthContext()

    const formRef = useRef<HTMLFormElement>(null)
    const [validatedForm, setValidatedForm] = useState(false)
    const [message, setMessage] = useState("")
    const [processing, setProcessing] = useState<boolean>(false)

    const initFormData: IEInvoiceSettings = {
        eInvoiceProviderName: "",
        eInvoiceConfigurations: "",
        eInvoiceAutoPublish: false,
        eInvoiceEnabled: false,
        issueInvoiceAfterMinutes: null,
        invoiceIssueStartDate: null
    }

    const [formData, setFormData] = useState(initFormData)

    const loadFormData = async () => {
        if (props.providerId && props.objectName && props.objectId && props.show) {
            if (props.objectName == "Place") {
                const res = await PlaceService.get(props.providerId, props.objectId)
                if (res?.isSuccess) {
                    setFormData({
                        eInvoiceProviderName: res.data.eInvoiceProviderName,
                        eInvoiceConfigurations: res.data.eInvoiceConfigurations,
                        eInvoiceEnabled: res.data.eInvoiceEnabled,
                        eInvoiceAutoPublish: res.data.eInvoiceAutoPublish,
                        issueInvoiceAfterMinutes: res.data.issueInvoiceAfterMinutes,
                        invoiceIssueStartDate: res.data.invoiceIssueStartDate
                    })
                } else {
                    console.log(res?.message)
                }
            } else if (props.objectName == "Event") {
                const res = await EventService.get(props.providerId, props.objectId)
                if (res?.isSuccess) {
                    setFormData({
                        eInvoiceProviderName: res.data.eInvoiceProviderName,
                        eInvoiceConfigurations: res.data.eInvoiceConfigurations,
                        eInvoiceEnabled: res.data.eInvoiceEnabled,
                        eInvoiceAutoPublish: res.data.eInvoiceAutoPublish,
                        issueInvoiceAfterMinutes: res.data.issueInvoiceAfterMinutes,
                        invoiceIssueStartDate: res.data.invoiceIssueStartDate
                    })
                } else {
                    console.log(res?.message)
                }
            }
        }
    }

    useEffect(() => {
        setMessage("")
        loadFormData()
    }, [props.providerId, props.objectName, props.objectId, props.show])

    const handleFormControlChange = (e: ChangeEvent<HTMLInputElement>) => {
        var name = e.target.name
        var value = e.target.value
        if (name === "eInvoiceEnabled" || name === "eInvoiceAutoPublish") {
            setFormData({ ...formData, [name]: e.target.checked })
        } else if (name === "issueInvoiceAfterMinutes") {
            setFormData({ ...formData, [name]: value ? parseInt(value) : null })
        } else if (name === "invoiceIssueStartDate") {
            setFormData({ ...formData, [name]: value ? value : null })
        } else {
            setFormData({ ...formData, [name]: value })
        }
    }

    const handleSave = async (e: SyntheticEvent) => {
        e.preventDefault()

        setMessage("")

        if (!processing) {
            setProcessing(true)
            if (props.providerId && props.objectId) {
                if (formRef.current?.checkValidity()) {
                    try {
                        if (props.objectName === "Place") {
                            const res = await PlaceService.updateEInvoiceSettings(props.providerId, props.objectId, formData)
                            if (res?.isSuccess) {
                                props.onClose()
                            } else {
                                setMessage(res?.message)
                            }
                        } else if (props.objectName === "Event") {
                            const res = await EventService.updateEInvoiceSettings(props.providerId, props.objectId, formData)
                            if (res?.isSuccess) {
                                props.onClose()
                            } else {
                                setMessage(res?.message)
                            }
                        }
                    } catch (error) {
                        console.log(error)
                    }
                } else {
                    setValidatedForm(true)
                }
            }
            setProcessing(false)
        }
    }

    return (
        <Modal show={props.show} onHide={props.onClose} backdrop="static" keyboard={false} size="lg">
            <Modal.Header closeButton>
                <Modal.Title><i className="fas fa-file-invoice-dollar"></i> E-Invoice Settings <span>|</span> {props.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="pb-0">
                {message !== "" && <Alert variant="info" onClose={() => setMessage("")} dismissible>{message}</Alert>}
                <EInvoiceProviderSelect required={false} value={formData.eInvoiceProviderName || ""} onChange={handleFormControlChange}></EInvoiceProviderSelect>
                <Form noValidate validated={validatedForm} ref={formRef as RefObject<HTMLFormElement>}>
                    <Form.Group className="mb-3" controlId="eInvoiceConfigurations">
                        <Form.Label>Configurations</Form.Label>
                        <Form.Control as="textarea"
                            placeholder={EInvoiceConfigurationExamples[formData.eInvoiceProviderName || ""]}
                            rows={15}
                            name="eInvoiceConfigurations"
                            value={formData.eInvoiceConfigurations || ""}
                            onChange={handleFormControlChange} />
                    </Form.Group>
                </Form>
                <Form.Check type="checkbox" id="eInvoiceEnabled" name="eInvoiceEnabled" label="Enabled" checked={formData.eInvoiceEnabled || false} onChange={handleFormControlChange} />
                <Form.Check type="checkbox" id="eInvoiceAutoPublish" name="eInvoiceAutoPublish" label="Auto Issue Invoice" checked={formData.eInvoiceAutoPublish || false} onChange={handleFormControlChange} />
                <Form.Group className="mt-3" controlId="issueInvoiceAfterMinutes">
                    <Form.Label>Issue Invoice After Minutes</Form.Label>
                    <Form.Control type="number" name="issueInvoiceAfterMinutes" value={formData.issueInvoiceAfterMinutes || ""} onChange={handleFormControlChange} />
                </Form.Group>
                <Form.Group className="mb-3" controlId="invoiceIssueStartDate">
                    <Form.Label>Invoice Issue Start Date</Form.Label>
                    <DatePicker name="invoiceIssueStartDate" value={formData.invoiceIssueStartDate || ""} onChange={handleFormControlChange}></DatePicker>
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="light" onClick={props.onClose}><i className="fas fa-times"></i> Cancel</Button>
                {((props.objectName === "Event" && hasPermission(Permissions.Events.EInvoiceSettings)) ||
                    (props.objectName === "Place" && hasPermission(Permissions.Places.EInvoiceSettings))) &&
                    <Button variant="info" onClick={handleSave} disabled={processing}>
                        {processing ? <><Spinner animation="border" size="sm" /> Saving...</> : <><i className="fas fa-save"></i> Save</>}
                    </Button>
                }
            </Modal.Footer>
        </Modal>)
}

export default EInvoiceSettingsModal
